import ExerciseSupport from '../../../student/components/exercises/ExerciseSupport';
import { keyframes } from '@emotion/react';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import React from 'react';
import { blankOut, ConversationExercise, ConversationLine, ConversationMode, shows } from '../../../student/mockLessonContent';
import { BLUE, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT, SURFACE_SOFT } from '../../../student/studentStyles';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import SpeakingHero from '../../../student/components/exercises/SpeakingHero';
import { cardSx } from '../../../student/studentStyles';
import RecordAnswerPanel from '../../../student/components/exercises/RecordAnswerPanel';
import TeacherNote from '../../../student/components/exercises/TeacherNote';
import MediaErrorNotice from '../../../student/components/MediaErrorNotice';
import useMockMediaFailure from '../../../student/components/useMockMediaFailure';
import { ExerciseReady, useExerciseLayout } from './exerciseLayout';

const speakerPulse = keyframes`
    0% { opacity: 1; }
    50% { opacity: 0.45; }
    100% { opacity: 1; }
`;

// How long each line plays in the fake audio.
const LINE_MS = 2200;

const labelByMode: Record<ConversationMode, string> = {
    listen: 'LISTEN',
    listenNoCharacters: 'LISTEN ONLY · NO TEXT',
    listenSpeak: 'LISTEN, THEN SPEAK',
    readSpeak: 'READ & SPEAK',
    readBlankSpeak: 'FILL THE GAPS OUT LOUD',
    speakOnly: 'FROM MEMORY',
};

const speakingModes: ConversationMode[] = ['listenSpeak', 'readSpeak', 'readBlankSpeak', 'speakOnly'];

type LineStatus = 'heard' | 'passed' | 'moved';

interface ConversationExerciseCardProps {
    exercise: ConversationExercise;
    onReady: ExerciseReady;
}

// One card for all six conversation prompt classes. The whole script is visible,
// but lines unlock in order. Listening modes: every line must be heard. Speaking
// modes: the student says every line on both sides. Pass or Move on (after Medium
// or two unsuccessful takes) resolves only that line before the next unlocks.
export default function ConversationExerciseCard({ exercise, onReady }: ConversationExerciseCardProps) {
    const desktop = useExerciseLayout() === 'desktop';
    const [statuses, setStatuses] = React.useState<LineStatus[]>([]);
    const [playingLine, setPlayingLine] = React.useState<number | null>(null);
    const [playingAll, setPlayingAll] = React.useState(false);
    const timeoutRef = React.useRef<number>();
    const takeFailure = useMockMediaFailure();
    const [audioRetry, setAudioRetry] = React.useState<{ index: number; all: boolean } | null>(null);

    React.useEffect(() => () => window.clearTimeout(timeoutRef.current), []);

    const mode = exercise.mode;
    const lines = exercise.lines;
    const isSpeakingMode = speakingModes.includes(mode);
    const hideText = mode === 'listenNoCharacters';
    // Reading and memory modes give no model audio — that would hand over the answer.
    const canPlayAudio = mode === 'listen' || mode === 'listenNoCharacters' || mode === 'listenSpeak';
    const firstSpeaker = lines[0]?.speakerHanzi;
    const progress = statuses.length;
    const finished = progress >= lines.length;

    // Only the current line can complete; replays of earlier lines change nothing.
    const markDone = (index: number, status: LineStatus) => {
        setStatuses(prev => (index === prev.length ? [...prev, status] : prev));
    };

    const onReadyRef = React.useRef(onReady);
    onReadyRef.current = onReady;
    React.useEffect(() => {
        if (lines.length > 0 && statuses.length === lines.length) {
            onReadyRef.current('done');
        }
    }, [statuses.length, lines.length]);

    const stopPlayback = () => {
        window.clearTimeout(timeoutRef.current);
        setPlayingAll(false);
        setPlayingLine(null);
    };

    const playFrom = (index: number, all: boolean) => {
        window.clearTimeout(timeoutRef.current);
        if (takeFailure('audio')) {
            stopPlayback();
            setAudioRetry({ index, all });
            return;
        }
        setAudioRetry(null);
        setPlayingAll(all);
        if (index >= lines.length) {
            stopPlayback();
            return;
        }
        setPlayingLine(index);
        timeoutRef.current = window.setTimeout(() => {
            // In the listening modes, hearing the current line to the end completes it.
            if (!isSpeakingMode) {
                markDone(index, 'heard');
            }
            if (all) {
                playFrom(index + 1, true);
            } else {
                setPlayingLine(null);
            }
        }, LINE_MS);
    };

    const handlePlayAll = () => {
        if (playingAll) {
            stopPlayback();
        } else {
            setPlayingAll(true);
            playFrom(0, true);
        }
    };

    const isBlanked = (line: ConversationLine) =>
        (mode === 'readBlankSpeak' && line.blankHanzi != null) || (mode === 'speakOnly' && line.speakerHanzi !== firstSpeaker);

    const displayHanzi = (line: ConversationLine) => {
        if (mode === 'readBlankSpeak') {
            return line.blankHanzi ?? line.hanzi;
        }
        if (mode === 'speakOnly' && line.speakerHanzi !== firstSpeaker) {
            return blankOut(line.hanzi);
        }
        return line.hanzi;
    };

    const instruction = isSpeakingMode
        ? `Say every line in order — both speakers${mode === 'speakOnly' ? ', blanked lines from memory' : ''}. (${progress} of ${lines.length} done)`
        : `Listen to every line in order to continue. (${progress} of ${lines.length} heard)`;

    return (
        <Box sx={{ ...(isSpeakingMode ? { ...cardSx, overflow: 'hidden' } : {}), '@media (prefers-reduced-motion: reduce)': { '&, & *': { animation: 'none !important', transition: 'none !important' } } }}>
            {isSpeakingMode && <SpeakingHero label={labelByMode[mode]} title={exercise.title} />}
            <Box sx={isSpeakingMode ? { p: desktop ? 3 : 2.5 } : undefined}>
            {!isSpeakingMode && <ExerciseLabel>{labelByMode[mode]}</ExerciseLabel>}
            {audioRetry && <Box sx={{ mb: 2 }}><MediaErrorNotice failure="audio" onRetry={() => playFrom(audioRetry.index, audioRetry.all)} /></Box>}
            {exercise.teacher?.intro && <Box sx={{ mb: 2 }}><TeacherNote text={exercise.teacher.intro} /></Box>}
            {!isSpeakingMode && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 20, color: NAVY, flex: 1, lineHeight: 1.3 }}>
                    {exercise.title}
                </Typography>
                {canPlayAudio && !isSpeakingMode && (
                    <Button
                        onClick={handlePlayAll}
                        startIcon={playingAll ? <StopRoundedIcon /> : <PlayArrowRoundedIcon />}
                        sx={{
                            fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, textTransform: 'none',
                            border: `1px solid ${RING_TRACK}`, borderRadius: '999px', px: 1.75, py: 0.5,
                            whiteSpace: 'nowrap', flexShrink: 0, minHeight: 44,
                        }}
                    >
                        {playingAll ? 'Stop' : 'Play all'}
                    </Button>
                )}
            </Box>}
            <Typography sx={{ fontFamily: INTER, fontSize: 13, color: finished ? SUCCESS_TEXT : SLATE, mb: 2 }}>
                {finished ? (exercise.teacher?.correct ?? 'Conversation complete — nice work!') : instruction}
            </Typography>
            <Stack spacing={1.5}>
                {lines.map((line, index) => {
                    const status = statuses[index];
                    const isCurrent = index === progress;
                    const isLocked = index > progress;
                    const isActive = playingLine === index;
                    const isRight = line.side === 'right';
                    const blanked = isBlanked(line);
                    const recording = isSpeakingMode && isCurrent;
                    return (
                        <Box key={`${line.hanzi}-${index}`} sx={{ display: 'flex', justifyContent: isRight ? 'flex-end' : 'flex-start' }}>
                            <Box sx={{
                                width: recording ? (desktop ? '82%' : '95%') : undefined,
                                maxWidth: desktop ? (recording ? '82%' : '72%') : (recording ? '95%' : '85%'),
                                p: desktop ? 2 : 1.75,
                                borderRadius: isRight ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                backgroundColor: isRight ? `${BLUE}14` : SURFACE_SOFT,
                                border: `2px solid ${isActive || recording ? BLUE : 'transparent'}`,
                                opacity: isLocked ? 0.45 : 1,
                                transition: 'border-color 150ms ease, opacity 150ms ease',
                                display: 'flex', alignItems: 'flex-start', gap: 1,
                            }}>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 12, color: SLATE }}>
                                            {line.speakerHanzi} · {line.speakerEnglish}
                                        </Typography>
                                        {status && (
                                            <Chip
                                                label={status === 'heard' ? 'HEARD' : status === 'passed' ? 'PASSED' : 'MOVED ON'}
                                                size="small"
                                                sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', color: status === 'moved' ? SLATE : SUCCESS_TEXT, backgroundColor: status === 'moved' ? `${SLATE}1A` : `${GREEN}1A` }}
                                            />
                                        )}
                                        {recording && (
                                            <Chip
                                                label="YOUR TURN"
                                                size="small"
                                                sx={{ height: 22, fontFamily: INTER, fontWeight: 700, fontSize: 11, letterSpacing: '0.06em', color: BLUE, backgroundColor: `${BLUE}1F` }}
                                            />
                                        )}
                                    </Stack>
                                    {hideText ? (
                                        <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.5, fontStyle: 'italic' }}>
                                            Audio only — listen for the meaning
                                        </Typography>
                                    ) : (
                                        <>
                                            <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 16, color: NAVY, mt: 0.25, lineHeight: 1.4, letterSpacing: blanked ? '0.08em' : 'normal' }}>
                                                {displayHanzi(line)}
                                            </Typography>
                                            {shows(exercise, 'pinyin') && !blanked && (
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{line.pinyin}</Typography>
                                            )}
                                            {shows(exercise, 'english') && !(mode === 'speakOnly' && blanked) && (
                                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{line.english}</Typography>
                                            )}
                                        </>
                                    )}
                                    {isSpeakingMode && !isLocked && (
                                        <>
                                            <RecordAnswerPanel
                                                key={index}
                                                grades={line.grades}
                                                takes={line.takes ?? ['good']}
                                                idleLabel="Record this line"
                                                compact
                                                disabled={!isCurrent}
                                                correctText={exercise.teacher?.spokenCorrect}
                                                incorrectText={exercise.teacher?.spokenIncorrect}
                                                onComplete={() => markDone(index, 'passed')}
                                                onMoveOn={() => markDone(index, 'moved')}
                                            />
                                        </>
                                    )}
                                </Box>
                                {canPlayAudio && (
                                    <IconButton
                                        aria-label={`Play line ${index + 1}`}
                                        disabled={isLocked}
                                        onClick={() => { setPlayingAll(false); playFrom(index, false); }}
                                        sx={{
                                            width: 44, height: 44, color: BLUE, flexShrink: 0,
                                            border: !isSpeakingMode && isCurrent ? `1px solid ${BLUE}` : '1px solid transparent',
                                        }}
                                    >
                                        {isActive
                                            ? <VolumeUpRoundedIcon sx={{ fontSize: 18, animation: `${speakerPulse} 900ms ease-in-out infinite` }} />
                                            : <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />}
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Stack>
            <ExerciseSupport exercise={exercise} />
            </Box>
        </Box>
    );
}
